import { useEffect, useState } from 'react';

import { db, FieldValue } from 'src/utils/firebase';

/**
 * Custom hook for managing scenarios in Firestore.
 * @param {string} userId - The user ID to fetch scenarios for.
 * @returns {object} An object containing scenarios array and CRUD operations.
 */
const useScenarioService = (userId) => {
  // State to hold scenarios
  const [scenarios, setScenarios] = useState([]);

  // Effect to subscribe to changes in the scenarios collection
  useEffect(() => {
    // Check if the user is authenticated before querying for accounts
    if (!userId) {
      setScenarios([]); // Set accounts to an empty array when the user is not authenticated
      return () => {}; // Exit the useEffect early
    }

    // Subscribe to changes in the scenarios collection for the current user
    const unsubscribe = db.collection(`users/${userId}/scenarios`).onSnapshot((snapshot) => {
      // Extract scenario data from snapshot and update state
      const scenariosData = snapshot.docs.map((doc) => ({
        id: doc.id ?? 'default',
        name: 'Base Case',
        ...doc.data(),
      }));
      setScenarios(scenariosData);
    });

    // Unsubscribe from snapshot listener when component unmounts
    return () => unsubscribe();
  }, [userId]); // Re-run effect when userId changes

  // Function to create a new scenario
  const createScenario = async (scenario) => {
    const result = await db.collection(`users/${userId}/scenarios`).add(scenario);
    return result;
  };

  // Function to update an existing scenario or create it if it doesn't exist
  const updateScenario = async (scenarioId, newData) => {
    const scenarioRef = db.doc(`users/${userId}/scenarios/${scenarioId}`);

    // Prepare updates and fields to remove
    const updates = {};
    const fieldsToDelete = [];

    Object.entries(newData).forEach(([key, value]) => {
      // If the value is null/undefined/empty string, mark it for deletion
      if (value === null || value === undefined || value === '') {
        fieldsToDelete.push(key);
      }
      // Only add non-empty objects to updates
      else if (!(typeof value === 'object' && Object.keys(value).length === 0)) {
        updates[key] = value;
      }
    });

    // First, update with new values
    if (Object.keys(updates).length > 0) {
      await scenarioRef.set(updates, { merge: true });
    }

    // Then, remove null fields using FieldValue.delete()
    if (fieldsToDelete.length > 0) {
      const deleteUpdates = fieldsToDelete.reduce((acc, field) => {
        acc[field] = FieldValue.delete();
        return acc;
      }, {});

      await scenarioRef.update(deleteUpdates);
    }
  };

  // Function to delete a scenario
  const deleteScenario = async (scenarioId) => {
    await db.doc(`users/${userId}/scenarios/${scenarioId}`).delete();
    setScenarios(scenarios.filter((scenario) => scenario.id !== scenarioId));
  };

  // Return scenarios array and CRUD operations
  return { scenarios, createScenario, updateScenario, deleteScenario };
};

export default useScenarioService;
